import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-decimal-input',
  standalone: false,
  templateUrl: './decimal-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DecimalInputComponent),
      multi: true
    }
  ]
})
export class DecimalInputComponent implements ControlValueAccessor {
  @Input() readonly = false;
  @Input() placeholder = '';
  @Input() inputId: string | null = null;

  public displayValue = '';
  public isDisabled = false;

  private onChange: (value: number | null) => void = () => { };
  private onTouched: () => void = () => { };

  public writeValue(value: number | null): void {
    this.displayValue = this.formatForDisplay(value);
  }

  public registerOnChange(onChange: (value: number | null) => void): void {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public onInput(inputElement: HTMLInputElement): void {
    const sanitizedValue = this.sanitize(inputElement.value);
    this.displayValue = sanitizedValue;
    inputElement.value = sanitizedValue;
    this.onChange(this.parse(sanitizedValue));
  }

  public onBlur(): void {
    const formattedValue = this.formatForDisplay(this.parse(this.displayValue));
    if (!formattedValue.includes('e'))
      this.displayValue = formattedValue;

    this.onTouched();
  }

  private sanitize(rawValue: string): string {
    let sanitizedValue = rawValue.replace(/[^0-9.,-]/g, '');
    sanitizedValue = sanitizedValue.charAt(0) + sanitizedValue.slice(1).replace(/-/g, '');

    const separators = sanitizedValue.match(/[.,]/g) ?? [];
    if (separators.length > 1 && separators.every(separator => separator === separators[0]))
      return sanitizedValue.replace(/[.,]/g, '');

    const lastSeparatorIndex = Math.max(sanitizedValue.lastIndexOf(','), sanitizedValue.lastIndexOf('.'));
    if (lastSeparatorIndex === -1)
      return sanitizedValue;

    const integerPart = sanitizedValue.slice(0, lastSeparatorIndex).replace(/[.,]/g, '');
    const decimalPart = sanitizedValue.slice(lastSeparatorIndex + 1);
    return `${integerPart},${decimalPart}`;
  }

  private parse(value: string): number | null {
    const normalizedValue = value.replace(',', '.');
    if (normalizedValue === '' || normalizedValue === '-' || normalizedValue === '.')
      return null;

    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  private formatForDisplay(value: number | null): string {
    return value === null || value === undefined ? '' : String(value).replace('.', ',');
  }
}
